import React from 'react';

import './css/WhoWeAre.css'

import { useCursorRimLight } from './rimLight';

const INTRO_TEXT: string = 'We spent years in image processing and machine learning. We\'re building Underlayer because platforms that distribute images and video at scale have no practical way to trace leaks or meet upcoming transparency rules, without shipping their data to a third party.'

const linkedInSrc: string = require( './images/Icon.LinkedIn.Light.png' )

interface Founder {
	name: string
	role: string
	background: string
	email: string
	photoSrc: string
	linkedInUrl: string
	websiteUrl?: string
	websiteLogoSrc?: string
}

const FOUNDERS: Founder[] = [
	{
		name: 'Clement Gerber',
		role: 'Co-founder · Engineering',
		background: 'Senior software engineer, image processing and high-performance C++ architecture, ten years in the industry.',
		email: 'clement@underlayer.ai',
		photoSrc: require( './images/Team.Clement.png' ),
		linkedInUrl: 'https://www.linkedin.com/in/clément-gerber-6ba699128/',
		websiteUrl: 'https://clementg.dev',
		websiteLogoSrc: require( './images/logos/Logo.CG.Dark.png' ),
	},
	{
		name: 'Justin Reverdi',
		role: 'Co-founder · Research',
		background: 'Research engineer. PhD in machine learning (Université de Toulouse) on ML robustness for the aeronautics industry.',
		email: 'justin@underlayer.ai',
		photoSrc: require( './images/Team.Justin.png' ),
		linkedInUrl: 'https://www.linkedin.com/in/justin-reverdi-aa49b6177/',
	},
]


function MailIcon(): React.JSX.Element {
	return (
		<svg className="WhoWeAre-Email-Icon" viewBox="0 0 16 16" fill="none" aria-hidden="true" >
			<rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
			<path d="M2 4.5 8 9l6-4.5" />
		</svg>
	)
}

// One founder: the photo beside the name, the role and a line of background,
// then the address and the logos of their pages elsewhere under a hairline.
function FounderCard( { founder, index }: { founder: Founder, index: number } ): React.JSX.Element {

	return (
		<div className="Tile WhoWeAre-Card" data-fade data-fade-delay={ ( 0.15 + index * 0.08 ).toFixed( 2 ) } >
			<img className="WhoWeAre-Photo" src={ founder.photoSrc } alt={ founder.name } />
			<div className="WhoWeAre-Body" >
				<div className="WhoWeAre-Name" >{ founder.name }</div>
				<div className="WhoWeAre-Role" >{ founder.role }</div>
				<div className="WhoWeAre-Background fs-16" >{ founder.background }</div>
				<div className="WhoWeAre-Links" >
					<a className="WhoWeAre-Email" href={ `mailto:${ founder.email }` } >
						<MailIcon />
						{ founder.email }
					</a>
					<div className="WhoWeAre-Logos" >
						<a className="WhoWeAre-Logo" href={ founder.linkedInUrl } target="_blank" rel="noopener noreferrer" >
							<img src={ linkedInSrc } alt="LinkedIn" />
						</a>
						{ founder.websiteUrl && founder.websiteLogoSrc && (
							<a className="WhoWeAre-Logo" href={ founder.websiteUrl } target="_blank" rel="noopener noreferrer" >
								<img className="WhoWeAre-WebsiteLogo" src={ founder.websiteLogoSrc } alt={ `${ founder.name } website` } />
							</a>
						) }
					</div>
				</div>
			</div>
		</div>
	)
}


// The founders, each with their own address: the contact page's second
// section, under the call and the shared inbox.
export function WhoWeAre(): React.JSX.Element {

	const rootRef = React.useRef<HTMLDivElement>( null )
	useCursorRimLight( rootRef, '.Tile' )

	return (
		<div className="Pane-Light Page-Section WhoWeAre" ref={ rootRef } >
			<div className="Container" >
				<div className="WhoWeAre-Row" >

					<div className="WhoWeAre-Column" >
						<div className="Page-Label" data-fade >Who you'll talk to</div>
						<div className="Page-Heading" data-fade data-fade-delay="0.05" >Built by two engineers. Answered by the same two.</div>
						<div className="WhoWeAre-Text Page-Text fs-18" data-fade data-fade-delay="0.1" >{ INTRO_TEXT }</div>
					</div>

					<div className="WhoWeAre-Cards" >
						{ FOUNDERS.map( ( founder: Founder, index: number ): React.JSX.Element => (
							<FounderCard founder={ founder } index={ index } key={ founder.name } />
						) ) }
					</div>

				</div>
			</div>
		</div>
	)
}
